import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiOutlineArrowLeft } from "react-icons/hi2";
import { Button, DialogForm, GroupedTabs, PageLayout } from "@components";
import { panelSm, textPageTitle } from "@design-system";
import { useWorkspaceData } from "@data";
import { useWorkspace } from "../../context/WorkspaceContext";
import { DriverProfileForm } from "../../components/Driver/DriverProfileForm";
import { useDrivers } from "../../hooks";
import { useCanManageWorkspace } from "../../hooks/useCanManageWorkspace";
import { Driver } from "../../models";
import { DriverAvatar } from "../../components/Driver/DriverAvatar";
import { DriverTimeEntries } from "../../components/Driver/DriverTimeEntries";

export default function DriverDetailPage() {
  const { driverId } = useParams();
  const [params, setParams] = useSearchParams();
  const { t } = useTranslation("driver");
  const { activeWorkspace } = useWorkspace();
  const workspaceId = activeWorkspace?.workspaceId || "";
  const canManage = useCanManageWorkspace(workspaceId);
  const { drivers, companies, vehicles, loading } = useWorkspaceData();
  const query = useDrivers();
  const [dirty, setDirty] = useState(false);
  const [pendingTab, setPendingTab] = useState<string | null>(null);
  useEffect(() => {
    setDirty(false);
    setPendingTab(null);
  }, [workspaceId, driverId]);
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  const driver = (query.data ?? drivers).find(
    (item) => item.id === driverId && item.workspaceId === workspaceId,
  );
  const back = (
    <Link
      to="/resources/drivers"
      className="inline-flex items-center gap-2 text-sm font-medium text-secondary hover:text-brand"
    >
      <HiOutlineArrowLeft className="h-4 w-4" />
      {t("profile.back")}
    </Link>
  );
  if ((loading.drivers && !query.data) || query.isLoading)
    return (
      <PageLayout>
        <div className="w-full space-y-4">
          {back}
          <p role="status">{t("profile.loading")}</p>
        </div>
      </PageLayout>
    );
  if (query.isError || !driver)
    return (
      <PageLayout>
        <div className="w-full space-y-4">
          {back}
          <p role="alert">
            {t(query.isError ? "profile.load_error" : "profile.not_found")}
          </p>
          {query.isError && (
            <Button onClick={() => query.refetch()}>
              {t("profile.refresh")}
            </Button>
          )}
        </div>
      </PageLayout>
    );
  const tabs = [
    {
      id: "overview",
      label: t("profile.overview"),
      content: (
        <DriverProfileForm
          key={`${workspaceId}:${driver.id}`}
          driver={driver}
          companies={companies}
          vehicles={vehicles}
          drivers={query.data ?? drivers}
          canManage={canManage}
          onSaved={() => query.refetch()}
          onDirty={setDirty}
        />
      ),
    },
    ...(canManage
      ? [
          {
            id: "time",
            label: t("profile.time"),
            content: (
              <DriverTimeEntries
                key={`${workspaceId}:${driver.id}`}
                workspaceId={workspaceId}
                driverId={driver.id}
              />
            ),
          },
        ]
      : []),
  ];
  const tab = tabs.some((item) => item.id === params.get("tab"))
    ? params.get("tab")!
    : "overview";
  return (
    <PageLayout>
      <div className="w-full space-y-4">
        {back}
        <section className={`${panelSm} p-4 sm:p-6`}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex min-w-0 items-center gap-4">
              <DriverAvatar
                driverId={driver.id}
                workspaceId={workspaceId}
                name={Driver.getDriverName(driver, true)}
                size={64}
              />
              <div className="min-w-0">
                <h1 className={`${textPageTitle} truncate`}>
                  {Driver.getDriverName(driver, true)}
                </h1>
                <p className="mt-2 text-sm text-secondary">
                  {
                    companies.find((company) => company.id === driver.companyId)
                      ?.name
                  }
                </p>
                {driver.onDuty !== undefined && (
                  <p className="mt-1 text-sm text-secondary">
                    {t(driver.onDuty ? "status.on_duty" : "status.off_duty")}
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>
        <section className={panelSm}>
          <GroupedTabs
            key={`${workspaceId}:${driver.id}`}
            variant="detail"
            tabs={tabs}
            initialTabId={tab}
            selectedTabId={tab}
            onTabChange={(value) => {
              if (dirty && value !== tab) setPendingTab(value);
              else setParams({ tab: value });
            }}
            ariaLabel={t("profile.tabs")}
          />
        </section>
        <DialogForm
          show={pendingTab !== null}
          title={t("profile.discard_title")}
          toggleModal={() => setPendingTab(null)}
          confirmButton={() => {
            if (pendingTab) setParams({ tab: pendingTab });
            setDirty(false);
            setPendingTab(null);
          }}
          buttonConfirmText={t("profile.discard")}
          buttonCloseText={t("profile.keep_editing")}
        />
      </div>
    </PageLayout>
  );
}
